import { Component, DestroyRef, NgZone, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs';
import { DayStore } from '../today/day.store';
import {
  SCHEDULE_GROUP_DEFAULT_LABELS,
  groupPageCommands,
  isGroupPageUrl,
  resolveScheduleGroupLabel
} from '../today/schedule-group.util';
import { computeActiveElapsedMs, computePlanMs, formatElapsedMs, formatOverrunWords } from './timer-elapsed.util';

/**
 * Nav-bar pill for the running (or paused) time block, shown on every signed-in page except group
 * pages, which show the timer themselves. It links to the running activity's group page with the
 * timer card open. Built from the design system's lm-pill (NavBar component).
 *
 * The time ticks every second, but the pill is a plain link with an aria-label rather than a live
 * region, so screen readers aren't sent every tick.
 */
@Component({
  selector: 'app-running-pill',
  standalone: true,
  imports: [RouterLink],
  template: `
    @if (visible()) {
      <a
        class="lm-pill"
        [class.lm-pill--attention]="attention()"
        [routerLink]="link()"
        [queryParams]="{ timer: 'open' }"
        [attr.aria-label]="ariaLabel()"
        [attr.title]="'Open in ' + groupLabel()">
        <span class="lm-dot" aria-hidden="true"></span>
        <span class="lm-pill__name">{{ name() }}</span>
        <span>{{ timeLabel() }}</span>
      </a>
    }
  `
})
export class RunningPillComponent {
  private readonly store = inject(DayStore);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);

  private readonly url = signal(this.router.url);
  private readonly now = signal(Date.now());
  private tickId: ReturnType<typeof setInterval> | null = null;

  readonly block = computed(() => this.store.day()?.runningBlock ?? null);

  readonly visible = computed(() => !!this.block() && !isGroupPageUrl(this.url()));

  readonly name = computed(() => {
    const block = this.block();
    return block ? this.store.getActivityName(block.activityTypeId) : '';
  });

  private readonly ordinal = computed(() => {
    const block = this.block();
    if (!block) {
      return null;
    }
    const activity = this.store.activityTypes().find((candidate) => candidate.id === block.activityTypeId);
    return activity?.kind === 'TIME_BLOCK' ? activity.scheduleType : null;
  });

  readonly groupLabel = computed(() => {
    const ordinal = this.ordinal();
    if (!ordinal) {
      return 'Today';
    }
    const group = this.store.scheduleGroups().find((candidate) => candidate.ordinal === ordinal);
    return group ? resolveScheduleGroupLabel(group) : SCHEDULE_GROUP_DEFAULT_LABELS[ordinal];
  });

  readonly link = computed(() => {
    const ordinal = this.ordinal();
    return ordinal ? groupPageCommands(ordinal) : ['/today'];
  });

  private readonly elapsedMs = computed(() => {
    const block = this.block();
    return block ? computeActiveElapsedMs(block, this.now()) : 0;
  });

  private readonly paused = computed(() => !!this.block()?.pausedAt);

  private readonly overrunMs = computed(() => {
    const block = this.block();
    if (!block || this.paused()) {
      return 0;
    }
    const planMs = computePlanMs(block.durationMinutes);
    return planMs > 0 ? Math.max(0, this.elapsedMs() - planMs) : 0;
  });

  readonly attention = computed(() => this.paused() || this.overrunMs() > 0);

  readonly timeLabel = computed(() => {
    const elapsed = formatElapsedMs(this.elapsedMs());
    if (this.paused()) {
      return `Paused · ${elapsed}`;
    }
    const overrun = this.overrunMs();
    return overrun > 0 ? `+${formatElapsedMs(overrun)} over` : elapsed;
  });

  readonly ariaLabel = computed(() => {
    const elapsed = formatElapsedMs(this.elapsedMs());
    const state = this.paused()
      ? `paused at ${elapsed}`
      : this.overrunMs() > 0
        ? `planned time complete, ${formatOverrunWords(this.overrunMs())}`
        : `running, ${elapsed}`;
    return `${this.name()}, ${state}. Open in ${this.groupLabel()}`;
  });

  constructor() {
    this.syncWithUrl(this.router.url);
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed()
    ).subscribe((event) => this.syncWithUrl(event.urlAfterRedirects));

    // Tick once a second only while a block is shown and still counting.
    effect(() => {
      const ticking = this.visible() && !this.paused();
      if (ticking && this.tickId === null) {
        this.now.set(Date.now());
        // Outside Angular's zone: a one-second interval would otherwise keep the app from ever being
        // "stable" (tests, testability). The signal update still schedules change detection.
        this.tickId = this.zone.runOutsideAngular(() => setInterval(() => this.now.set(Date.now()), 1000));
      } else if (!ticking && this.tickId !== null) {
        this.stopTicking();
      }
    });
    this.destroyRef.onDestroy(() => this.stopTicking());
  }

  private syncWithUrl(url: string): void {
    this.url.set(url);
    this.now.set(Date.now());
    if (isGroupPageUrl(url)) {
      return;
    }
    const path = url.split('?')[0];
    // Today loads the day itself; only top up when it already has fresh data (e.g. back from a detail page).
    if (path === '/today' && !this.store.hasFreshDayData()) {
      return;
    }
    this.store.refreshForShell();
  }

  private stopTicking(): void {
    if (this.tickId !== null) {
      clearInterval(this.tickId);
      this.tickId = null;
    }
  }
}
